import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import Icon from 'components/DesignSystem/Icon';
import Snackbar from 'components/DesignSystem/Snackbar';
import useStudyRowActions from 'components/Flux/Upload/StudyRow/useStudyRowActions';
import { useGetUserQuery } from 'services/api/users';
import { useGetCompanyQuery } from 'services/api/companies';
import { useGetDownloadLinkQuery } from 'services/api/studies';
import { useReexecuteMutation } from 'services/api/products/flux';
import { useHandleRequestErrors } from 'utils/hooks';
import { ResponseError } from 'services/api/base';
import { isStudyFinished, isStudyRunning, ListedStudy } from 'helper/studies';
import { selectUserId, selectUserIsAdmin } from 'redux/reducers/authReducer';
import { cssVar } from 'constants/theme';

import './index.scss';

const STATUS_MODIFIER: Record<string, string> = {
  'Em Fila': 'queued',
  'Com Erro': 'error',
  'Estudo Pronto': 'ready',
  Cancelado: 'cancelled',
};

function formatDateTime(timestamp: number | null, fallback: string): string {
  if (timestamp === null) {
    return fallback;
  }

  return new Date(timestamp * 1000).toLocaleString('pt-BR');
}

export default function StudyCard({ study }: { study: ListedStudy }) {
  const horizonStart = new Date(study.dt_inicio * 1000).toLocaleDateString('pt-BR');
  const horizonEnd = new Date(study.dt_fim * 1000).toLocaleDateString('pt-BR');
  const lastExecution = formatDateTime(study.dt_last_execution, 'Não executado');
  const lastResponse = formatDateTime(study.dt_last_response, 'Não houve resposta');
  const model = study.cenarios?.[0]?.blocos?.[0]?.ds_modelo.split('/').at(-1);

  const { data: userData, error: userError } = useGetUserQuery({ id: study.id_user ?? 0 });
  const {
    data: companyData, error: companyError,
  } = useGetCompanyQuery({ companyId: study.id_empresa ?? 0 });
  const {
    data: downloadLink,
    error: downloadLinkError,
    isLoading: downloadLinkLoading,
  } = useGetDownloadLinkQuery({ id: study.id });
  const [triggerReexecute] = useReexecuteMutation();
  const userId = useSelector(selectUserId);
  const isAdmin = useSelector(selectUserIsAdmin);
  // Mesmas regras da StudyRow desktop: o backend decide a posse; aqui só não oferecemos
  // a ação a quem não é o dono
  const isOwner = isAdmin || (study.id_user !== null && study.id_user === userId);
  const running = isStudyRunning(study);
  // Com Cancelar na tela são quatro botões: Cancelar/Excluir ficam só no ícone
  const showCancel = isOwner && !isStudyFinished(study);
  const ownerButtonClass = `upload-study-card-action danger${showCancel ? ' icon-only' : ''}`;
  const {
    confirmationDialogBox, feedback, clearFeedback,
    confirmDelete, confirmCancel, deleting, cancelling,
  } = useStudyRowActions(study);

  useHandleRequestErrors(userError as ResponseError | undefined);
  useHandleRequestErrors(companyError as ResponseError | undefined);
  useHandleRequestErrors(downloadLinkError as ResponseError | undefined);

  const userName = [userData?.first_name, userData?.last_name].filter(Boolean).join(' ');

  return (
    <article className="upload-study-card">
      {confirmationDialogBox}
      {feedback && (
        <Snackbar
          className="m-2"
          variant={feedback.variant}
          closeButton
          onClose={clearFeedback}
        >
          {feedback.message}
        </Snackbar>
      )}

      <div className="upload-study-card-description">
        <h2 className="upload-study-card-name">{study.ds_nome_estudo}</h2>
        <p className="upload-study-card-company">{companyData?.razao_social}</p>
        <div className="upload-study-card-chips">
          {model && (
            <span className="upload-study-card-chip">
              <Icon iconType="layers" color={cssVar('text-muted')} size="small" />
              {model}
            </span>
          )}
          {userName && (
            <span className="upload-study-card-chip">
              <Icon iconType="user" color={cssVar('text-muted')} size="small" />
              {userName}
            </span>
          )}
        </div>
      </div>

      <div className="upload-study-card-horizon">
        <Icon iconType="calendar" color={cssVar('text-accent')} size="small" />
        <span className="upload-study-card-horizon-label">Horizonte</span>
        <span className="upload-study-card-horizon-value">
          {horizonStart}
          {' – '}
          {horizonEnd}
        </span>
      </div>

      <div className="upload-study-card-status">
        <span className={`upload-study-card-tag ${STATUS_MODIFIER[study.id_status] ?? ''}`}>
          <span className="upload-study-card-dot" />
          {study.id_status}
        </span>

        {study.ck_internal_error && (
          <div className="upload-study-card-warning">
            <Icon iconType="warning" color={cssVar('danger-fg')} size="small" />
            <span>
              Verifique seu e-mail de processamento interrompido para mais informações
            </span>
          </div>
        )}

        <dl className="upload-study-card-meta">
          <div>
            <dt>Última Execução</dt>
            <dd>{lastExecution}</dd>
          </div>
          <div>
            <dt>Última Resposta</dt>
            <dd>{lastResponse}</dd>
          </div>
          <div>
            <dt>Nº de Execuções</dt>
            <dd>{study.vl_execution_counter}</dd>
          </div>
        </dl>
      </div>

      <div className="upload-study-card-actions">
        <Link
          to={downloadLink || ''}
          className={`upload-study-card-action${downloadLinkLoading ? ' disabled' : ''}`}
          aria-disabled={downloadLinkLoading}
        >
          <Icon iconType="download" color={cssVar('text-strong')} size="small" />
          Baixar
        </Link>
        <button
          type="button"
          className="upload-study-card-action accent"
          onClick={() => { triggerReexecute(study.id); }}
        >
          <Icon iconType="refresh" color={cssVar('text-accent')} size="small" />
          Re-executar
        </button>
        {showCancel && (
          <button
            type="button"
            id={`cancel-study-${study.id}`}
            className={ownerButtonClass}
            disabled={cancelling}
            aria-label="Cancelar o estudo"
            title="Cancele a execução do estudo"
            onClick={confirmCancel}
          >
            <Icon iconType="close" color={cssVar('danger-fg')} size="small" />
          </button>
        )}
        {isOwner && (
          <button
            type="button"
            id={`delete-study-${study.id}`}
            className={ownerButtonClass}
            disabled={running || deleting}
            aria-label="Excluir o estudo"
            title={running ? 'Cancele o estudo antes de removê-lo' : 'Remova o estudo'}
            onClick={confirmDelete}
          >
            <Icon iconType="trash" color={cssVar('danger-fg')} size="small" />
            {!showCancel && 'Excluir'}
          </button>
        )}
      </div>
      {isOwner && running && (
        <p className="upload-study-card-hint">Cancele o estudo antes de removê-lo</p>
      )}
    </article>
  );
}
